/**
 * Home View Component
 *
 * Initial view showing asset rows for Workspace and Personal assets.
 * In tool mode, also displays an "Explore..." row with available media types.
 */

import { useState, useEffect, useMemo } from 'react';
import type { IAsset, ProviderRegistry } from '../providers';
import { ZapworksProvider } from '../providers/zapworks/ZapworksProvider';
import { useMediaAssets, useAssetSelection } from '../hooks';
import { MediaSidebar } from './MediaSidebar';
import { MediaHeader } from './MediaHeader';
import { AssetDetailPanel } from './AssetDetailPanel';
import type { Filters } from '../types';
import type { AssetSelectCallback, MediaType, MediaTypeCard, AssetSource, SortOption, AssetRowState } from '../types';
import { DEFAULT_MEDIA_TYPE_CARDS } from '../utils';
import { useAssetMetadata } from '../hooks';
import { ProcessingIcon } from '../../../icons';
import { Dropzone } from './Dropzone';
import { AssetRow } from './AssetRow';
import { EmptyStateSection } from './EmptyStateSection';
import { DragSelectContainer } from './DragSelectContainer';

export interface HomeViewProps {
  /** Provider registry containing the Zapworks provider */
  registry: ProviderRegistry;
  /** Workspace ID for filtering workspace assets */
  workspaceId: string;
  /** Available media types to display in the Explore row (tool mode only) */
  enabledMediaTypes?: MediaType[];
  /** Current media type being browsed */
  mediaType?: MediaType | null;
  /** Whether to show the Explore row with media types (tool mode) */
  showExploreRow?: boolean;
  /** Whether to show the sidebar navigation (tool mode) */
  showSidebar?: boolean;
  /** Enable multi-select mode for assets */
  enableMultiSelect?: boolean;
  /** Whether to show the multi-select toggle in the header */
  showMultiSelectToggle?: boolean;
  /** Callback to toggle multi-select mode */
  onToggleMultiSelect?: () => void;
  /** Callback when an asset is selected */
  onSelect?: AssetSelectCallback;
  /** Callback when "View all" is clicked for a section */
  onViewAll?: (section: 'workspace' | 'personal') => void;
  /** Callback when a media type is selected from Explore row */
  onSelectMediaType?: (mediaType: MediaType) => void;
  /** Callback when a source is selected from sidebar */
  onSourceSelect?: (source: AssetSource) => void;
  /** Callback to handle file uploads */
  handleUpload?: (files: FileList) => void;
  /** Current search query from parent */
  searchQuery?: string;
  /** Callback when search query changes */
  onSearchChange: (query: string) => void;
  /** Sort option from parent */
  sortBy: SortOption;
  /** Callback when sort changes */
  onSortChange?: (sort: SortOption) => void;
  /** Filters from parent */
  filters?: Filters;
  /** Callback when filters change */
  onFiltersChange?: (filters: Filters) => void;
  /** Callback to delete a single asset */
  onDelete?: (assetId: string, asset: IAsset) => Promise<boolean>;
  /** Callback to delete multiple assets */
  onDeleteMultiple?: (assetIds: string[]) => Promise<boolean>;
    /** Callback to open premium upload modal */
  onOpenPremiumUploadModal?: () => void;
  /** Changing this value triggers a re-fetch of assets */
  refreshKey?: number;
  setRefreshKey?: React.Dispatch<React.SetStateAction<number>>;
  /** Custom CSS class */
  className?: string;
}

export const HomeView = ({
  registry,
  workspaceId,
  enabledMediaTypes,
  mediaType,
  showExploreRow = false,
  showSidebar = false,
  enableMultiSelect = true,
  showMultiSelectToggle = false,
  onToggleMultiSelect,
  onSelect,
  onViewAll,
  onSelectMediaType,
  onSourceSelect,
  handleUpload,
  searchQuery = '',
  onSearchChange,
  sortBy: sortByProp,
  onSortChange,
  filters: filtersProp,
  onFiltersChange,
  onDelete,
  onDeleteMultiple,
  onOpenPremiumUploadModal,
  className = '',
  refreshKey,
  setRefreshKey,
}: HomeViewProps) => {
  const [activeSource, setActiveSource] = useState<AssetSource>('workspace-library');
  const sortBy = sortByProp;
  const filters: Filters = filtersProp ?? { animated: [], templateType: [], status: [], labels: [] };
  const [workspaceAssets, setWorkspaceAssets] = useState<AssetRowState>({
    assets: [],
    count: 0,
    loading: true,
  });
  const [personalAssets, setPersonalAssets] = useState<AssetRowState>({
    assets: [],
    count: 0,
    loading: true,
  });

  const { assets: workspaceAssetsList, loading: workspaceLoading, error: workspaceError, totalCount: workspaceTotalCount, updateAssetInList } = useMediaAssets({
    registry,
    mediaType: mediaType || enabledMediaTypes || undefined,
    workspaceId,
    source: 'workspace-library',
    searchQuery,
    sortBy,
    filters,
    refreshKey,
    limit: 6, // Load a small number to get count and metadata for Home view
  });

  // Get personal assets using useMediaAssets
  const { assets: personalAssetsList, loading: personalLoading, error: personalError, totalCount: personalTotalCount, updateAssetInList: updatePersonalAssetInList } = useMediaAssets({
    registry,
    mediaType: mediaType || enabledMediaTypes || undefined,
    source: 'user-library',
    searchQuery,
    sortBy,
    filters,
    refreshKey,
    limit: 6, // Load a small number to get count and metadata for Home view
  });

  const { hasAnimated: computedShowAnimatedFilter } = useAssetMetadata([...workspaceAssetsList, ...personalAssetsList]);

  // Get the Zapworks provider
  const zapworksProvider = registry.get('zapworks') as ZapworksProvider | undefined;

  const handleDeleteAsset = async (assetId: string, asset: IAsset) => {
    const success = await onDelete?.(assetId, asset);
    if (success) clearSelection();
  };

  const handleDeleteMultiple = async (assetIds: string[]) => {
    const success = await onDeleteMultiple?.(assetIds);
    if (success) clearSelection();
  };

  // Sync workspace assets from hook to state
  useEffect(() => {
    if (!zapworksProvider || !workspaceId) {
      setWorkspaceAssets({ assets: [], count: 0, loading: false });
      return;
    }
    setWorkspaceAssets({
      assets: workspaceAssetsList,
      count: workspaceTotalCount,
      loading: workspaceLoading,
      error: workspaceError,
    });
  }, [workspaceAssetsList, workspaceLoading, workspaceError, workspaceTotalCount]);

  // Sync personal assets from hook to state
  useEffect(() => {
    if (!zapworksProvider) {
      setPersonalAssets({ assets: [], count: 0, loading: false });
      return;
    }
    setPersonalAssets({
      assets: personalAssetsList,
      count: personalTotalCount,
      loading: personalLoading,
      error: personalError,
    });
  }, [personalAssetsList, personalLoading, personalError, personalTotalCount]);

  const handleAssetClick = (asset: IAsset) => {
    if (onSelect) {
      onSelect(asset);
    }
  };

  const handleViewAll = (section: 'workspace' | 'personal') => {
    if (onViewAll) {
      onViewAll(section);
    }
  };

  const handleMediaTypeClick = (mediaType: MediaType) => {
    if (onSelectMediaType) {
      onSelectMediaType(mediaType);
    }
  };

  const handleSourceChange = (source: AssetSource) => {
    setActiveSource(source);
    if (onSourceSelect) {
      onSourceSelect(source);
    }
  };

  // Filter media type cards based on enabled types
  const availableMediaTypes = DEFAULT_MEDIA_TYPE_CARDS.filter(card =>
    card.isEnabled && (!enabledMediaTypes || enabledMediaTypes.includes(card.type))
  );

  const visibleAssets = useMemo(() => {
    return workspaceAssetsList.concat(personalAssetsList);
  }, [workspaceAssetsList, personalAssetsList]);
  const {
    selectedAssetIds,
    selectedAssets: selectedAssetObjects,
    hiddenSelectedCount,
    toggleSelection,
    addToSelection,
    clearSelection,
  } = useAssetSelection<IAsset>({
    enableMultiSelect,
    visibleAssets,
  });

  return (
    <div className={`library-view ${showSidebar ? 'library-view--with-sidebar' : ''} ${className}`}>
      {showSidebar && (
        <div className="library-view__sidebar">
          <MediaSidebar
            registry={registry}
            mediaType={mediaType}
            showWorkspaceAssets={!!workspaceId}
            activeSource={activeSource}
            isHomeView={true}
            onSourceChange={handleSourceChange}
          />
        </div>
      )}

      <div className="library-view__main">
        <div className="library-view__header">
          <MediaHeader
            searchQuery={searchQuery}
            sortBy={sortBy}
            filters={filters}
            onSearchChange={onSearchChange}
            onSortChange={(s) => onSortChange?.(s)}
            onFiltersChange={(f) => onFiltersChange?.(f)}
            onUpload={handleUpload}
            acceptedFileTypes={mediaType ? [mediaType] : enabledMediaTypes}
            showAnimatedFilter={computedShowAnimatedFilter}
            showMultiSelectToggle={showMultiSelectToggle}
            enableMultiSelect={enableMultiSelect}
            onToggleMultiSelect={onToggleMultiSelect}
            onOpenPremiumUploadModal={onOpenPremiumUploadModal}
          />
        </div>
        <DragSelectContainer enabled={enableMultiSelect} onSelect={toggleSelection} onSelectMany={addToSelection}>
          {({ dragHighlightedIds }) => (
            <Dropzone
              onUpload={handleUpload}
            >
              <div className="library-view__content">
                {/* Empty State */}
                {(workspaceTotalCount === 0 && !workspaceAssets.loading) || (workspaceTotalCount === 0 && !workspaceAssets.loading && personalTotalCount === 0 && !personalAssets.loading) ? (
                  <>
                    <h3 className="library-view__row-title">Workspace ({workspaceTotalCount})</h3>
                    <EmptyStateSection sectionTitle="workspace" searchQuery={searchQuery} onUpload={handleUpload} onOpenPremiumUploadModal={onOpenPremiumUploadModal} acceptedFileTypes={mediaType ? [mediaType] : enabledMediaTypes} />
                  </>
                ) : null}
                {/* Loading */}
                {(workspaceAssets.loading || personalAssets.loading) ? (
                  <div className="library-view__loading">
                    <ProcessingIcon />
                  </div>
                ) : (
                  <>
                    {/* Workspace Assets Row */}
                    <AssetRow
                      title="Workspace"
                      count={workspaceTotalCount}
                      state={workspaceAssets}
                      enableMultiSelect={enableMultiSelect}
                      onAssetClick={handleAssetClick}
                      onAssetSelect={toggleSelection}
                      onViewAll={() => handleViewAll('workspace')}
                      selectedAssets={selectedAssetIds}
                      dragHighlightedIds={dragHighlightedIds}
                    />

                    {/* Personal Assets Row */}
                    <AssetRow
                      title="Personal (legacy)"
                      count={personalTotalCount}
                      state={personalAssets}
                      enableMultiSelect={enableMultiSelect}
                      onAssetClick={handleAssetClick}
                      onAssetSelect={toggleSelection}
                      onViewAll={() => handleViewAll('personal')}
                      selectedAssets={selectedAssetIds}
                      dragHighlightedIds={dragHighlightedIds}
                    />
                  </>
                )}
                {/* Explore Row (tool mode only) */}
                {showExploreRow && (
                  <ExploreRow
                    mediaTypes={availableMediaTypes}
                    onSelectMediaType={handleMediaTypeClick}
                  />
                )}
              </div>
            </Dropzone>
          )}
        </DragSelectContainer>
      </div>

      <div className="library-view__detail">
        <AssetDetailPanel
          selectedAssets={selectedAssetObjects}
          selectedAssetIds={selectedAssetIds}
          totalSelectedCount={selectedAssetIds.length}
          hiddenSelectedCount={hiddenSelectedCount}
          activeSource={activeSource}
          onDelete={handleDeleteAsset}
          onDeleteMultiple={handleDeleteMultiple}
          onDeselectAll={clearSelection}
          key={selectedAssetIds.join(',')} // Force remount when selection changes to reset internal state
          updateAssetInList={asset => {
            updateAssetInList(asset);
            updatePersonalAssetInList(asset);
          }}
          setRefreshKey={setRefreshKey}
        />
      </div>
    </div>
  );
};

interface ExploreRowProps {
  mediaTypes: MediaTypeCard[];
  onSelectMediaType: (mediaType: MediaType) => void;
}

const ExploreRow = ({ mediaTypes, onSelectMediaType }: ExploreRowProps) => {
  return (
    <div className="library-view__row library-view__row--explore">
      <div className="library-view__row-header">
        <h3 className="library-view__row-title">Explore...</h3>
      </div>

      <div className="library-view__row-content">
        <div className="library-view__media-types">
          {mediaTypes.map((card) => (
            <button
              key={card.type}
              className="library-view__media-type-card"
              onClick={() => onSelectMediaType(card.type)}
              type="button"
              aria-label={`Browse ${card.title}`}
            >
              <div
                className="library-view__media-type-preview"
                style={{ backgroundImage: `url(${card.previewImage})` }}
                aria-hidden="true"
              />
              <span className="library-view__media-type-title">{card.title}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
